import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Plus,
  Activity,
  AlertCircle,
  X,
  CheckCircle,
  Clock,
  AlertTriangle,
} from 'lucide-react'
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts'
import api, { getAllCattle } from '../../services/api'
import PageHeader from '../../components/PageHeader'
import clsx from 'clsx'

// ---------------------------------------------------------------------------
// API helpers
// ---------------------------------------------------------------------------

const getLactationDashboardData = (ranchId) =>
  api.get('/dairy/lactation-cycles/dashboard', { params: { ranch_id: ranchId } }).then(res => res.data)

const getLactationCycles = (ranchId, params = {}) =>
  api.get('/dairy/lactation-cycles', { params: { ranch_id: ranchId, ...params } }).then(res => res.data)

const createLactationCycle = (data) =>
  api.post('/dairy/lactation-cycles', data).then(res => res.data)

const updateLactationCycle = (id, data) =>
  api.patch(`/dairy/lactation-cycles/${id}`, data).then(res => res.data)

const getDairyCattle = (ranchId) =>
  getAllCattle(ranchId, { status: 'active', category: 'cow' })

// ---------------------------------------------------------------------------
// Stage colours
// ---------------------------------------------------------------------------

const stageColors = {
  early: '#3b82f6',
  peak: '#10b981',
  mid: '#f59e0b',
  late: '#ef4444',
  dry: '#6b7280',
}

const stageLabels = {
  early: 'Early',
  peak: 'Peak',
  mid: 'Mid',
  late: 'Late',
  dry: 'Dry',
}

// ---------------------------------------------------------------------------
// Start Lactation Modal
// ---------------------------------------------------------------------------

function StartLactationModal({ ranchId, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    ranch_id: ranchId,
    animal_id: '',
    calving_date: new Date().toISOString().split('T')[0],
    expected_lactation_days: 305,
  })
  const [error, setError] = useState('')

  const { data: cattleData } = useQuery({
    queryKey: ['dairyCattle', ranchId],
    queryFn: () => getDairyCattle(ranchId),
  })

  const mutation = useMutation({
    mutationFn: createLactationCycle,
    onSuccess: () => onSuccess(),
    onError: (err) => setError(err.response?.data?.detail || 'Failed to start lactation'),
  })

  const handleSubmit = (e) => {
    e.preventDefault()
    setError('')
    if (!formData.animal_id) {
      setError('Please select a cow')
      return
    }
    mutation.mutate({
      ...formData,
      expected_lactation_days: parseInt(formData.expected_lactation_days),
    })
  }

  const cows = cattleData || []

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-bold text-gray-900">Start New Lactation</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        {error && (
          <div className="mx-6 mt-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Cow *</label>
            <select
              required
              value={formData.animal_id}
              onChange={(e) => setFormData({ ...formData, animal_id: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            >
              <option value="">Select cow...</option>
              {cows.map((cow) => (
                <option key={cow.id} value={cow.id}>
                  {cow.local_name || cow.rfid_tag} ({cow.tag_id || cow.rfid_tag})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Calving Date *</label>
            <input
              type="date"
              required
              value={formData.calving_date}
              onChange={(e) => setFormData({ ...formData, calving_date: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Expected Lactation Days</label>
            <input
              type="number"
              min="180"
              max="500"
              value={formData.expected_lactation_days}
              onChange={(e) => setFormData({ ...formData, expected_lactation_days: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={mutation.isPending}
              className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50"
            >
              <CheckCircle className="h-4 w-4" />
              {mutation.isPending ? 'Starting...' : 'Start Lactation'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export default function LactationDashboard({ ranchId }) {
  const queryClient = useQueryClient()
  const [showNewModal, setShowNewModal] = useState(false)

  const { data: dashboard, isLoading: dashLoading } = useQuery({
    queryKey: ['lactationDashboard', ranchId],
    queryFn: () => getLactationDashboardData(ranchId),
  })

  const { data: cyclesData, isLoading: cyclesLoading } = useQuery({
    queryKey: ['lactationCycles', ranchId, 'active'],
    queryFn: () => getLactationCycles(ranchId, { is_active: true }),
  })

  const dryOffMutation = useMutation({
    mutationFn: ({ id }) => updateLactationCycle(id, { stage: 'dry' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lactationCycles', ranchId] })
      queryClient.invalidateQueries({ queryKey: ['lactationDashboard', ranchId] })
    },
  })

  const handleSuccess = () => {
    setShowNewModal(false)
    queryClient.invalidateQueries({ queryKey: ['lactationCycles', ranchId] })
    queryClient.invalidateQueries({ queryKey: ['lactationDashboard', ranchId] })
  }

  const isLoading = dashLoading || cyclesLoading

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  // Build ring chart data
  const stageCounts = dashboard?.stage_summary || {}
  const pieData = Object.entries(stageCounts)
    .filter(([, count]) => count > 0)
    .map(([stage, count]) => ({
      name: stageLabels[stage] || stage,
      value: count,
      color: stageColors[stage] || '#9ca3af',
    }))

  const totalActive = dashboard?.total_active ?? 0
  const avgDim = dashboard?.avg_dim ?? 0
  const dueDryOff = dashboard?.due_dry_off_soon ?? 0
  const overdueDryOff = dashboard?.overdue_dry_off ?? 0

  const cycles = cyclesData?.items || cyclesData || []

  return (
    <div className="space-y-6">
      <PageHeader
        title="Lactation Management"
        subtitle="Track lactation stages and performance"
        icon={Activity}
        breadcrumbs={[
          { label: 'Dairy', href: '/dairy' },
          { label: 'Lactation Overview' },
        ]}
        actions={
          <button
            onClick={() => setShowNewModal(true)}
            className="btn bg-emerald-600 text-white hover:bg-emerald-700"
          >
            <Plus className="h-4 w-4 mr-2" />
            Start New Lactation
          </button>
        }
      />

      {/* Stats Row + Ring Chart */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Ring Chart */}
        <div className="card p-6 lg:col-span-1">
          <h3 className="text-sm font-medium text-gray-700 mb-4">Lactation Stages</h3>
          {pieData.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              <Activity className="h-8 w-8 mx-auto mb-2" />
              <p className="text-sm">No active lactations</p>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={80}
                  paddingAngle={3}
                  dataKey="value"
                  label={({ name, value }) => `${name}: ${value}`}
                >
                  {pieData.map((entry, index) => (
                    <Cell key={index} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Stats */}
        <div className="lg:col-span-2 grid grid-cols-2 gap-4">
          <div className="card p-5">
            <div className="flex items-start gap-3">
              <div className="p-2.5 bg-green-50 rounded-xl ring-1 ring-green-100">
                <Activity className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900">{totalActive}</div>
                <div className="text-sm text-gray-500">Total Active</div>
              </div>
            </div>
          </div>
          <div className="card p-5">
            <div className="flex items-start gap-3">
              <div className="p-2.5 bg-blue-50 rounded-xl ring-1 ring-blue-100">
                <Clock className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900">{Math.round(avgDim)}</div>
                <div className="text-sm text-gray-500">Avg DIM</div>
              </div>
            </div>
          </div>
          <div className="card p-5">
            <div className="flex items-start gap-3">
              <div className="p-2.5 bg-amber-50 rounded-xl ring-1 ring-amber-100">
                <AlertTriangle className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900">{dueDryOff}</div>
                <div className="text-sm text-gray-500">Due for Dry-off</div>
              </div>
            </div>
          </div>
          <div className="card p-5">
            <div className="flex items-start gap-3">
              <div className="p-2.5 bg-red-50 rounded-xl ring-1 ring-red-100">
                <AlertCircle className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900">{overdueDryOff}</div>
                <div className="text-sm text-gray-500">Overdue Dry-off</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Active Lactations Table */}
      <div className="card overflow-hidden">
        <div className="px-6 py-4 border-b">
          <h3 className="text-lg font-medium text-gray-900">Active Lactations</h3>
        </div>

        {cycles.length === 0 ? (
          <div className="text-center py-12">
            <Activity className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900">No active lactations</h3>
            <p className="text-gray-500 mt-1">Start a lactation cycle to begin tracking</p>
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cow</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stage</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">DIM</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Total Yield (L)</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Peak Yield (L)</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Expected Dry-off</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {cycles.map((cycle) => {
                const stageBadgeColor = {
                  early: 'bg-blue-100 text-blue-800',
                  peak: 'bg-green-100 text-green-800',
                  mid: 'bg-amber-100 text-amber-800',
                  late: 'bg-red-100 text-red-800',
                  dry: 'bg-gray-100 text-gray-800',
                }

                return (
                  <tr key={cycle.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {cycle.animal_name || cycle.cow_name || 'Unknown'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={clsx(
                        'px-2 py-1 text-xs font-medium rounded-full capitalize',
                        stageBadgeColor[cycle.stage] || 'bg-gray-100 text-gray-800'
                      )}>
                        {cycle.stage}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 text-right">
                      {cycle.days_in_milk ?? cycle.dim ?? '--'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 text-right">
                      {cycle.total_yield != null ? Number(cycle.total_yield).toFixed(1) : '--'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 text-right">
                      {cycle.peak_yield != null ? Number(cycle.peak_yield).toFixed(1) : '--'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {cycle.expected_dry_off_date
                        ? new Date(cycle.expected_dry_off_date).toLocaleDateString()
                        : '--'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {cycle.stage !== 'dry' && (
                        <button
                          onClick={() => {
                            if (window.confirm(`Dry off ${cycle.animal_name || cycle.cow_name || 'this cow'}?`)) {
                              dryOffMutation.mutate({ id: cycle.id })
                            }
                          }}
                          disabled={dryOffMutation.isPending}
                          className="px-3 py-1 text-xs font-medium text-amber-700 bg-amber-50 rounded-lg hover:bg-amber-100 transition-colors"
                        >
                          Dry Off
                        </button>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal */}
      {showNewModal && (
        <StartLactationModal
          ranchId={ranchId}
          onClose={() => setShowNewModal(false)}
          onSuccess={handleSuccess}
        />
      )}
    </div>
  )
}
